/**
 * Notification type groups used for badges and mark-as-read on page visit.
 * Align with backend model (notification.js) and sidebar routes.
 */
export const ORDER_NOTIFICATION_TYPES = [
  "order_placed",
  "order_confirmed",
  "order_shipped",
  "order_delivered",
  "order_cancelled",
  "order_dispatch_reminder",
  "order_dispatch_due_today",
  "order_dispatch_delay_choice",
  "order_update",
  "order_review_prompt",
  "order_reviews_revealed",
];

/** All offer-related types (for utilities). */
export const OFFER_NOTIFICATION_TYPES = [
  "offer_received",
  "offer_accepted",
  "offer_rejected",
  "offer_countered",
];

/** My Offers (buyer): responses to offers you sent. */
export const OFFER_BUYER_TYPES = [
  "offer_accepted",
  "offer_rejected",
  "offer_countered",
];

/** Review Offers (seller): offers on your listings (new + buyer accepted counter). */
export const OFFER_SELLER_TYPES = ["offer_received", "offer_accepted"];

export const REFUND_NOTIFICATION_TYPES = [
  "order_refund",
  "refund_approved",
  "refund_rejected",
  "refund_resolved",
  "refund_offer_pending",
  "refund_offer_rejected",
  "return_required",
  "return_in_transit",
  "return_received",
];

/** Buyer updates on refund / return progress. */
export const BUYER_REFUND_UPDATE_TYPES = [
  "refund_approved",
  "refund_rejected",
  "refund_resolved",
  "refund_offer_pending",
  "refund_offer_rejected",
  "return_required",
  "return_received",
  "order_dispute_outcome",
];

/** Seller must review or act on these (refund requests, returns). */
export const SELLER_REFUND_REVIEW_TYPES = [
  "order_refund",
  "return_in_transit",
  "refund_offer_rejected",
  "refund_resolved",
  "return_received",
  "order_dispute_outcome",
];

export function countUnreadByTypes(notifications = [], types = []) {
  if (!Array.isArray(notifications) || !types.length) return 0;
  const set = new Set(types);
  return notifications.filter((notif) => !notif.read && set.has(notif.type)).length;
}

/**
 * Count unread notifications matching types, with an optional predicate
 * (e.g. buyer vs seller facing order alerts).
 */
export function countUnreadByTypesWhere(notifications = [], types = [], predicate) {
  if (!Array.isArray(notifications) || !types.length) return 0;
  const set = new Set(types);
  return notifications.filter((notif) => {
    if (notif.read || !set.has(notif.type)) return false;
    if (typeof predicate === "function" && !predicate(notif)) return false;
    return true;
  }).length;
}

/** Admin-facing order alerts (new orders, refunds). */
export const ADMIN_ORDER_NOTIFICATION_TYPES = [
  "admin_order",
  "admin_refund",
];

/** VA mediation — buyer escalations and outcomes. */
export const VA_MEDIATION_NOTIFICATION_TYPES = [
  "dispute_escalation",
  "order_dispute_outcome",
];

export const MESSAGE_NOTIFICATION_TYPES = ["message_received"];

/** Event-related notification types (admin/seller event submissions). */
export const EVENT_NOTIFICATION_TYPES = [
  "event_request",
  "event_approved",
  "event_rejected",
  "event_amended",
];

/** Veteran-related notification types (proof submissions + admin decisions). */
export const VETERAN_NOTIFICATION_TYPES = [
  "veteran_request",
  "veteran_verified",
  "veteran_rejected",
];

/** Tab filter keys for notifications page (users & admins). */
export const NOTIFICATION_TAB_KEYS = {
  ALL: "all",
  ORDERS: "orders",
  CUSTOMER_ENQUIRIES: "customer_enquiries",
  VA_MEDIATION: "va_mediation",
  EVENTS: "events",
  SERVICES: "services",
  VETERAN: "veteran",
  OTHERS: "others",
};

/** Map tab key -> list of notification types that belong in that tab. */
export const NOTIFICATION_TYPES_BY_TAB = {
  [NOTIFICATION_TAB_KEYS.ORDERS]: [
    ...ORDER_NOTIFICATION_TYPES,
    ...REFUND_NOTIFICATION_TYPES,
    ...ADMIN_ORDER_NOTIFICATION_TYPES,
  ],
  [NOTIFICATION_TAB_KEYS.CUSTOMER_ENQUIRIES]: MESSAGE_NOTIFICATION_TYPES,
  [NOTIFICATION_TAB_KEYS.VA_MEDIATION]: VA_MEDIATION_NOTIFICATION_TYPES,
  [NOTIFICATION_TAB_KEYS.EVENTS]: EVENT_NOTIFICATION_TYPES,
  [NOTIFICATION_TAB_KEYS.VETERAN]: VETERAN_NOTIFICATION_TYPES,
  [NOTIFICATION_TAB_KEYS.OTHERS]: [...OFFER_NOTIFICATION_TYPES],
};

/**
 * Service listings reuse event_* notification types; detect via link/title/message
 * (backend uses /admin-services, "service" in copy, etc.).
 */
export const isServiceEventNotification = (notification) => {
  const n = notification || {};
  const type = String(n.type || "");
  if (!EVENT_NOTIFICATION_TYPES.includes(type)) return false;
  const link = String(n.link || "");
  const title = String(n.title || "");
  const message = String(n.message || "");
  if (link.includes("/admin-services") || link.includes("/dashboard-services") || link.includes("/profile/services")) {
    return true;
  }
  if (/\bservice\b/i.test(title) || /\bservice\b/i.test(message)) return true;
  return false;
};

/** Returns the tab key for a given notification type. Pass full `notification` when type is event_* to resolve Services vs Events. */
export const getNotificationTab = (type, notification) => {
  if (!type) return NOTIFICATION_TAB_KEYS.OTHERS;
  if (
    ORDER_NOTIFICATION_TYPES.includes(type) ||
    REFUND_NOTIFICATION_TYPES.includes(type) ||
    ADMIN_ORDER_NOTIFICATION_TYPES.includes(type)
  )
    return NOTIFICATION_TAB_KEYS.ORDERS;
  if (MESSAGE_NOTIFICATION_TYPES.includes(type)) return NOTIFICATION_TAB_KEYS.CUSTOMER_ENQUIRIES;
  if (VA_MEDIATION_NOTIFICATION_TYPES.includes(type)) return NOTIFICATION_TAB_KEYS.VA_MEDIATION;
  if (EVENT_NOTIFICATION_TYPES.includes(type)) {
    if (notification && isServiceEventNotification(notification)) return NOTIFICATION_TAB_KEYS.SERVICES;
    return NOTIFICATION_TAB_KEYS.EVENTS;
  }
  if (VETERAN_NOTIFICATION_TYPES.includes(type)) return NOTIFICATION_TAB_KEYS.VETERAN;
  return NOTIFICATION_TAB_KEYS.OTHERS;
};

export const isOrderType = (type) => ORDER_NOTIFICATION_TYPES.includes(type);
export const isOfferType = (type) => OFFER_NOTIFICATION_TYPES.includes(type);
export const isRefundType = (type) => REFUND_NOTIFICATION_TYPES.includes(type);
export const isMessageType = (type) => MESSAGE_NOTIFICATION_TYPES.includes(type);

/** Lifecycle order notifications: at most one per type + related order in the UI. */
export const SINGLE_PER_RELATED_NOTIFICATION_TYPES = [
  "order_placed",
  "order_confirmed",
  "order_shipped",
  "order_delivered",
  "order_cancelled",
  "order_dispatch_reminder",
  "order_dispatch_due_today",
  "order_dispatch_delay_choice",
];

export function dedupeLifecycleNotifications(notifications = []) {
  const singleTypes = new Set(SINGLE_PER_RELATED_NOTIFICATION_TYPES);
  const lifecycleByKey = new Map();
  const others = [];

  for (const notif of notifications) {
    const relatedId = notif.relatedId?.toString?.() || notif.relatedId || "";
    if (singleTypes.has(notif.type) && relatedId) {
      const key = `${notif.type}:${relatedId}`;
      const current = lifecycleByKey.get(key);
      if (!current) {
        lifecycleByKey.set(key, notif);
        continue;
      }
      const currentTime = new Date(current.createdAt || 0).getTime();
      const newTime = new Date(notif.createdAt || 0).getTime();
      const newer = newTime >= currentTime ? notif : current;
      const older = newer === notif ? current : notif;
      lifecycleByKey.set(key, {
        ...newer,
        read: Boolean(newer.read || older.read),
      });
    } else {
      others.push(notif);
    }
  }

  return [...lifecycleByKey.values(), ...others].sort(
    (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
  );
}
